import type {StoreConfig, SyncableStore} from "@/stores/plugins/sync/types";
import {toRaw} from "vue";
import {extractSelectedProperties} from "@/stores/plugins/sync/helper";
import {getMasterWindowSyncedStores, setupMasterWindowSync} from "@/stores/plugins/sync/masterWindow";
import {getSlaveWindowSyncedStores, setupSlaveWindowSync} from "@/stores/plugins/sync/slaveWindow";
import type {PiniaPlugin} from "pinia";
import {SYNC_CONFIG} from "@/stores/plugins/sync/config";

/**
 * Add two methods to the store:
 *
 * `getSyncState`: Returns the current state of the store, optionally filtered by properties.
 * `updateFromSync`: Updates the store state from a payload, optionally filtered by properties.
 */
const enhanceStoreWithSyncMethods = (store: SyncableStore, storeConfig: StoreConfig) => {
    store._isSyncing = false;

    store.getSyncState = function (this: SyncableStore) {
        const rawState = toRaw(this.$state) as Record<string, unknown>;

        if (storeConfig.properties) {
            return extractSelectedProperties(rawState, storeConfig.properties);
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {_isSyncing: _, ...state} = rawState;

        return state;
    };

    store.updateFromSync = function (this: SyncableStore, payload: Record<string, unknown>) {
        this._isSyncing = true;

        try {
            const updateData = storeConfig.properties
                ? extractSelectedProperties(payload, storeConfig.properties)
                : payload;

            this.$patch(updateData);
        } finally {
            setTimeout(() => {
                this._isSyncing = false;
            });
        }
    };
};

/**
 * Sets up a cleanup function that will be called when the window is unloaded.
 * This function removes all store listeners and clears the synced stores.
 */
function setupCleanup(): void {
    window.addEventListener('beforeunload', cleanup);
}

/**
 * Cleans up the sync plugin by removing all listeners and clearing synced stores.
 */
const cleanup = () => {
    if (window.ipcRenderer?.removeStoreListeners) {
        window.ipcRenderer.removeStoreListeners();
    }

    getMasterWindowSyncedStores().clear();
    getSlaveWindowSyncedStores().clear();

    window._syncRequestHandlerSetup = false;
    window._storeUpdateHandlerSetup = false;
    window._initialSyncRequested = false;
};

export const createSyncPlugin = (isMasterWindow = true): PiniaPlugin => {
    setupCleanup();

    return ({store}) => {
        const storeConfig = SYNC_CONFIG.stores[store.$id];

        if (!storeConfig || !storeConfig.enabled) {
            return;
        }

        const syncableStore = store as SyncableStore;

        enhanceStoreWithSyncMethods(syncableStore, storeConfig);

        if (isMasterWindow) {
            setupMasterWindowSync(syncableStore);
        } else {
            setupSlaveWindowSync(syncableStore);
        }

        return () => {
            if (isMasterWindow) {
                getMasterWindowSyncedStores().delete(syncableStore.$id);
            } else {
                getSlaveWindowSyncedStores().delete(syncableStore.$id);
            }
        };
    };
};
